import { SessionData, SessionTableRow, Subroutine } from '@/types/analytics';
import { formatNumber } from '@/util/number';

export const isFirstRow = (row: number, sessionData: SessionData) =>
  sessionData.subroutines.some(({ first_row }) => first_row === row);

export const getSubroutine = (subId: number, sessionData: SessionData) =>
  sessionData.subroutines.find((sub) => sub.sub_id === subId);

export const getDuration = (line: SessionTableRow, sessionData: SessionData) => {
  if (line.expanded !== 1) return formatNumber(line.duration);
  const currentSub = getSubroutine(line.sub_id, sessionData);
  return formatNumber(currentSub?.duration || 0);
};

const getSubRows = (subId: number, expandFully: boolean, lockedSubroutines: string[], sessionData: SessionData) => {
  const ignored: number[] = [];
  const currentSub = getSubroutine(subId, sessionData);

  if (!currentSub) return [];

  const result: SessionTableRow[] = [];

  currentSub.allrows.forEach((row) => {
    if (ignored.includes(row) || currentSub.first_row === row) return;

    const line = sessionData.lines.find((line) => line.row === row);
    if (!line) return;

    if (line.sub_id === subId) {
      result.push({
        ...line,
        sub_name: '  '.repeat(currentSub.depth) + line.sub_name,
        expanded: 0
      });
    } else {
      const targetSub = sessionData.subroutines.find(({ sub_id }) => sub_id === line.sub_id);

      if (targetSub && targetSub.depth >= currentSub.depth) {
        result.push({
          ...line,
          sub_name: '  '.repeat(targetSub?.depth || 0) + line.sub_name,
          expanded: targetSub.first_row === line.row ? (expandFully ? 2 : 1) : 0
        });

        (!expandFully || lockedSubroutines.includes(targetSub.name)) && ignored.push(...targetSub.allrows);
      }
    }
  });

  return result;
};

export const expandRow = (
  line: SessionTableRow,
  expandFully: boolean,
  lines: SessionTableRow[],
  lockedSubroutines: string[],
  sessionData: SessionData
): SessionTableRow[] => {
  if (lockedSubroutines.includes(line.sub_name.trim())) return lines;

  const subRows = getSubRows(line.sub_id, expandFully, lockedSubroutines, sessionData);
  const index = sessionData.lines.findIndex(({ row }) => row === line.row);
  const result = lines.toSpliced(index, 0, ...subRows);

  return result.map((value) => {
    if (value.row !== line.row) return value;
    return { ...value, expanded: 2 };
  });
};

export const collapseRow = (row: number, sub: Subroutine | undefined, lines: SessionTableRow[]): SessionTableRow[] => {
  if (!sub) return lines;

  const result = lines.filter((line) => !(sub.allrows.includes(line.row) && line.row !== sub.first_row));
  return result.map((value) => {
    if (value.row !== row) return value;
    return { ...value, expanded: 1 };
  });
};

export const collapseRowsByName = (
  subName: string,
  lines: SessionTableRow[],
  sessionData: SessionData
): SessionTableRow[] => {
  let result: SessionTableRow[] = lines;
  const subroutines = sessionData.subroutines.filter((sub) => sub.name === subName);

  subroutines.forEach((currentSub) => {
    result = result.filter((line) => !(currentSub.allrows.includes(line.row) && line.row !== currentSub.first_row));
  });

  return result.map((value) => {
    if (isFirstRow(value.row, sessionData) && value.sub_name.trim() === subName)
      return { ...value, expanded: 1 } as SessionTableRow;
    return value;
  });
};

export const expandAll = (sessionData: SessionData, lockedSubroutines: string[]): SessionTableRow[] => {
  const ignored: number[] = [];
  const result: SessionTableRow[] = [];

  sessionData.lines.forEach((line) => {
    if (ignored.includes(line.row)) return;

    const currentSub = sessionData.subroutines.find(({ sub_id }) => sub_id === line.sub_id);
    if (!currentSub) return;

    if (lockedSubroutines.includes(line.sub_name)) ignored.push(...currentSub.allrows);

    const isLocked = lockedSubroutines.includes(currentSub.name);
    const isMainRow = currentSub?.first_row === line.row;
    if (isLocked && !isMainRow) return;

    result.push({
      ...line,
      expanded: isMainRow ? (!isLocked ? 2 : 1) : 0,
      sub_name: '  '.repeat(currentSub?.depth || 0) + line.sub_name
    });
  });
  return result;
};

export const collapseAll = (sessionData: SessionData): SessionTableRow[] => {
  const result: SessionTableRow[] = [];

  sessionData.tasks.forEach((task) => {
    const line = sessionData.lines.find((line) => line.row === task.first_row);
    const sub = sessionData.subroutines.find((sub) => sub.sub_id === line?.sub_id);
    if (!line || !sub) return;

    result.push({
      ...line,
      expanded: 1
    });
  });

  return result;
};
